import { P2GLink } from '@parcel2go/core-ui';
import {
  BasketRequirementExplicit,
  BasketRequirementMultiple,
  QuoteRequirement,
  QuoteRequirementSection
} from '../types/basket-types';
import { CustomsKeys } from '../types/customs';
import { RequirementTypes } from '../types/enums';

export const MapRequirement = (
  x: QuoteRequirement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  basketSectionData: { [key: string]: any },
  defaultModel: string
) => ({ ...x, required: IsRequirementRequired(x, basketSectionData, defaultModel) });

export const IsRequirementRequired = (
  x: QuoteRequirement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  basketSectionData: { [key: string]: any },
  defaultModel: string
): boolean => {
  const keys = Object.keys(x?.metadata?.requiredIf || {});

  if (keys.length === 0) {
    return x.required;
  }

  let newRequired = false;
  if (keys.includes('all')) {
    // ALL
    const req = x.metadata.requiredIf as BasketRequirementMultiple;
    newRequired =
      req.all?.every((e) => {
        const v = e.value || req.value || [];
        const model =
          e.field.indexOf('.') > -1
            ? { model: e.field.split('.')[0], field: e.field.split('.')[1] }
            : { model: defaultModel, field: e.field };
        return v.includes(basketSectionData[model.model]?.[model.field]);
      }) || newRequired;
  } else if (keys.includes('any')) {
    // ANY
    const req = x.metadata.requiredIf as BasketRequirementMultiple;
    newRequired =
      req.any?.some((e) => {
        const v = e.value || req.value || [];
        const model =
          e.field.indexOf('.') > -1
            ? { model: e.field.split('.')[0], field: e.field.split('.')[1] }
            : { model: defaultModel, field: e.field };
        return v.includes(basketSectionData[model.model]?.[model.field]);
      }) || newRequired;
  } else {
    // SINGLE VALUE
    const req = x.metadata.requiredIf as BasketRequirementExplicit;
    const reqVal = Array.isArray(req.value) ? req.value : [req.value];
    const model =
      req.field.indexOf('.') > -1
        ? { model: req.field.split('.')[0], field: req.field.split('.')[1] }
        : { model: defaultModel, field: req.field };
    newRequired = reqVal.includes(basketSectionData[model.model]?.[model.field]) || newRequired;
  }

  return !!(x.required || newRequired);
};

export const SwapRequirementsFromSelectedExtras = (
  type: RequirementTypes,
  additionalRequirements: QuoteRequirement[],
  masterRequirementSection: QuoteRequirementSection
) => {
  // Swap out some requirements based on selected extras. Eg Phone ->  Mobile when SMS is selected.
  additionalRequirements?.forEach((x) => {
    const r = {
      requirementType: (x?.key.split('.')[0] || '') as RequirementTypes,
      requirementKey: x?.key.split('.')[1] || ''
    };

    // Just look at the requirements for the current requirementType
    if (type === r.requirementType && r.requirementKey && r.requirementType) {
      if (masterRequirementSection?.rules?.some((x) => x.key === r.requirementKey)) {
        // If exists, find and replace certain fields. Don't change the key or the full model.
        masterRequirementSection.rules.forEach((rule) => {
          if (rule.key === r.requirementKey) {
            rule.required = x.required;
            rule.name = x.name;
            rule.metadata = { ...rule.metadata, ...(x.metadata || {}) };
          }
        });
      } else {
        // Add new requirement
        masterRequirementSection.rules?.push(x);
      }
    }
  });
};

export const AddRuleExamples = (
  masterRequirementSection: QuoteRequirementSection,
  type: RequirementTypes.ORIGIN | RequirementTypes.DESTINATION,
  country: string
) => {
  masterRequirementSection?.rules
    ?.filter((rule) => rule.metadata && !rule.metadata['example'])
    .forEach((rule) => {
      switch (rule.key) {
        case CustomsKeys.Address.EORI:
          if (type === RequirementTypes.ORIGIN && country.startsWith('GBR')) {
            rule.metadata['example'] = 'EORI number must be GB followed by 12 numbers';
            rule.metadata.helperText = (
              <>
                Don't have a EORI number? Apply for one{' '}
                <P2GLink href="https://www.gov.uk/eori" target="_blank" dataTestId="apply-uk-eori-link">
                  https://www.gov.uk/eori
                </P2GLink>
              </>
            );
          } else {
            rule.metadata['example'] = 'EORI number must be a country code followed by at least 7 numbers';
          }
          break;
      }
    });
};
